import { Ty } from '../ty';
import { Assert, assertOk } from '@cotera/utilities';
import _ from 'lodash';
import { err, ok, Result } from 'neverthrow';

type UnionResult = Result<
  Ty.ExtendedAttributeType,
  { t: 'incompatible-types' }
>;

/**
 * Returns a ExtendedAttributeType that is "loose" union of the input types if
 *  - there is at least one input type
 *  - all the inputs' attribute types (.ty fields) are "compatible" with each other.
 *
 * Attribute types are compatible with each other if any of the following hold:
 *  - they are all enum types
 *  - they are all primitive types and all equivalent
 *  - they are all range types and all equivalent
 *  - they are all array types and their base types are compatible
 *  - they are all record types and their base types are all compatible
 *  - they are all struct types and any fields common to any subset of structs have compatible types.
 *
 * The returned union type is "loose", because the era type system is not yet
 * powerful enough to express true unions. e.g. for struct types, the fields are
 * just combined (and optional ones are made nullable).
 *
 * "tags" are merged.
 *
 * TODO: preserve object references in tys as desired in Schema
 */
export function tryUnion(tys: Ty.ExtendedAttributeType[]): UnionResult {
  if (tys.length === 0) {
    return err({ t: 'incompatible-types' });
  }

  const firstTy = Assert.assertExists(tys[0]);

  const nullable = tys.some((ty) => ty.nullable);
  const tags = [...new Set(tys.flatMap((ty) => ty.tags))];

  const attributeTypes = tys
    .map((ty) => ty.ty)
    .filter((ty) => ty.k === firstTy.ty.k);

  if (attributeTypes.length !== tys.length) {
    return err({ t: 'incompatible-types' });
  }

  switch (firstTy.ty.k) {
    case 'primitive': {
      const primitives = attributeTypes as Ty.PrimitiveType[];

      if (allStrictlyEqual(primitives.map((p) => p.t))) {
        return ok({
          ty: Assert.assertExists(primitives[0]), // by tys.length !== 0
          nullable,
          tags,
        });
      } else {
        return err({ t: 'incompatible-types' });
      }
    }

    case 'array': {
      const arrays = attributeTypes as Ty.ArrayType[];

      return tryUnion(arrays.map((array) => array.t)).map((mergedBaseTy) => ({
        ty: { k: 'array', t: mergedBaseTy },
        nullable,
        tags,
      }));
    }

    case 'enum': {
      const enumTys = attributeTypes as Ty.EnumType[];
      return ok({
        ty: { k: 'enum', t: [...new Set(enumTys.flatMap((ty) => ty.t))] },
        nullable,
        tags,
      });
    }

    case 'range': {
      const ranges = attributeTypes as Ty.RangeType[];

      if (
        allStrictlyEqual(ranges.map((r) => r.start)) &&
        allStrictlyEqual(ranges.map((r) => r.end)) &&
        allStrictlyEqual(ranges.map((r) => r.t))
      ) {
        return ok({
          ty: Assert.assertExists(ranges[0]), // by tys.length !== 0
          nullable,
          tags,
        });
      }

      return err({ t: 'incompatible-types' });
    }

    case 'record': {
      const records = attributeTypes as Ty.RecordType[];

      return tryUnion(records.map((record) => record.t)).map(
        (mergedBaseTy) => ({
          ty: { k: 'record', t: mergedBaseTy },
          nullable,
          tags,
        })
      );
    }

    case 'struct': {
      const structs = attributeTypes as Ty.StructType[];

      return tryUnionStructs(structs).map((mergedStructs) => ({
        ty: mergedStructs,
        nullable,
        tags,
      }));
    }

    default:
      return Assert.unreachable(firstTy.ty);
  }
}

function tryUnionStructs(
  structs: Ty.StructType[]
): Result<Ty.StructType, { t: 'incompatible-types' }> {
  // The set of keys per struct
  const keySets = structs.map((struct) => new Set(Object.keys(struct.fields)));

  const allKeys = [
    ...new Set(structs.flatMap((struct) => Object.keys(struct.fields))),
  ];

  // a "required" key is one that exists as a field for every struct
  const requiredKeys = new Set(
    allKeys.filter((key) => keySets.every((keySet) => keySet.has(key)))
  );

  // any key that is not "required"
  const optionalKeys = allKeys.filter((key) => !requiredKeys.has(key));

  const requiredFieldsResults: [key: string, UnionResult][] = [
    ...requiredKeys,
  ].map((key) => [
    key,
    tryUnion(
      structs.map((struct) =>
        Assert.assertExists(
          struct.fields[key] // exists by key being required
        )
      )
    ),
  ]);

  if (requiredFieldsResults.some(([_key, res]) => res.isErr())) {
    return err({ t: 'incompatible-types' });
  }

  const requiredFields = requiredFieldsResults.map(
    ([key, res]) => [key, assertOk(res)] as const
  );

  const optionalFieldsResults: [key: string, UnionResult][] = optionalKeys.map(
    (key) => {
      const fieldTys = structs
        .map((struct) => struct.fields[key])
        .filter((ty) => ty !== undefined);

      const unionFieldTysRes = tryUnion(fieldTys).map((mergedFieldTy) => ({
        ...mergedFieldTy,
        nullable: true,
      }));

      return [key, unionFieldTysRes];
    }
  );

  if (optionalFieldsResults.some(([_key, res]) => res.isErr())) {
    return err({ t: 'incompatible-types' });
  }

  const optionalFields = optionalFieldsResults.map(
    ([key, res]) => [key, assertOk(res)] as const
  );

  return ok({
    k: 'struct',
    fields: Object.fromEntries([...requiredFields, ...optionalFields]),
  });
}

/**
 * Returns whether all elements are strictly equal to each other.
 *
 * An empty set is vacuously all equal.
 */
export const allStrictlyEqual = (elts: any[]): boolean =>
  new Set(elts).size <= 1;
